import React from 'react';
import { css } from '@emotion/react';
import { FormattedMessage } from 'react-intl';
import { BridgedAlert as Alert } from 'components/common/feedback/bridged-alert';
import styled from 'types/theme.types';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { useDesignMode } from 'hooks/use-design-mode';
import { useUpdateUserPreferences } from 'stores/rq/user-preferences';
import { EventNames, track } from 'utils/analytics.utils';

const NewLookBanner: React.FunctionComponent = () => {
  const { selectedDesign, isDesignSwitchAvailable, isResolved, preferences, setMode } = useDesignMode();
  const { mutate: updatePreferences } = useUpdateUserPreferences();

  const shouldShow =
    isResolved && isDesignSwitchAvailable && selectedDesign === 'new' && !preferences?.newLookBannerDismissedAt;

  if (!shouldShow) return null;

  const dismiss = () => {
    track(EventNames.NEW_LOOK_BANNER_DISMISSED);
    updatePreferences({ newLookBannerDismissed: true });
  };

  const switchToClassic = () => setMode('legacy', 'banner', { newLookBannerDismissed: true });

  return (
    <StyledAlert
      data-e2e="new-look-banner"
      type="info"
      banner
      showIcon={false}
      closable
      onClose={dismiss}
      message={
        <BannerContent>
          <BannerTitle>
            <FormattedMessage id="new-look.banner.title" />
          </BannerTitle>
          <span>
            <FormattedMessage id="new-look.banner.message" />
          </span>
          <BannerAction type="button" data-e2e="new-look-banner-dismiss" onClick={dismiss}>
            <FormattedMessage id="new-look.banner.dismiss" />
          </BannerAction>
          <BannerAction type="button" data-e2e="new-look-banner-switch-back" onClick={switchToClassic}>
            <FormattedMessage id="new-look.banner.switch-back" />
          </BannerAction>
        </BannerContent>
      }
    />
  );
};

/* Same accent treatment as the announcement banner: this is an announcement, not a status message,
   so it keeps `surface.primary.action` with `text.onAction` on top in both modes. */
const StyledAlert = styled(Alert)`
  ${legacyOnly(
    theme => css`
      &.ant-alert {
        padding: 6px 20px;
        background-color: ${theme.limeYellow};
      }

      .ant-alert-message {
        color: ${theme.secondaryBlack};
        font-size: 14px;
      }

      .ant-alert-close-icon {
        color: ${theme.secondaryBlack};

        &:hover {
          opacity: 0.6;
        }
      }
    `
  )}

  ${newDesignOnly(css`
    &.ant-alert {
      background-color: var(--surface-primary-action);
    }

    .ant-alert-message,
    .ant-alert-close-icon {
      color: var(--text-on-action);
    }

    .ant-alert-close-icon:hover {
      opacity: 0.6;
    }
  `)}
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const BannerTitle = styled.span`
  font-weight: 600;
`;

const BannerAction = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  ${legacyOnly(
    theme => css`
      color: ${theme.secondaryBlack};
    `
  )}
  ${newDesignOnly(css`
    color: var(--text-on-action);
  `)}

  &:hover {
    opacity: 0.7;
  }
`;

export default NewLookBanner;
